import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { cookies } from '@/core/cookies';
import { env } from '@/core/env';
import { navigateTo } from '@/core/navigation';

/** What the sign-in form should do next after a password has been accepted. */
export type SignInResult = 'signed-in' | 'new-password-required';

const MFA_MESSAGE = 'This account requires multi-factor authentication, which this sign-in does not support yet.';

/**
 * Signs users in to Cognito from the app's own login page using SRP. The Cognito library keeps the tokens in
 * localStorage (same keys as the Angular app, so a session is shared between both apps) and refreshes the access
 * token from the refresh token whenever getSession() finds it expired.
 */
export class AuthService {
  private readonly pool = new CognitoUserPool({
    // stsServerUrl ends with the pool id: https://cognito-idp.<region>.amazonaws.com/<poolId>
    UserPoolId: env.stsServerUrl.split('/').pop() as string,
    ClientId: env.clientId,
  });

  /** Held between the first sign-in step and the new-password step of a first login. */
  private pendingUser: CognitoUser | null = null;
  private pendingAttributes: Record<string, string> = {};

  /** Signs in. Resolves 'new-password-required' when an admin-created account must set a password. */
  signIn(username: string, password: string): Promise<SignInResult> {
    const user = new CognitoUser({ Username: username.trim(), Pool: this.pool });
    const details = new AuthenticationDetails({ Username: username.trim(), Password: password });

    return new Promise<SignInResult>((resolve, reject) => {
      user.authenticateUser(details, {
        onSuccess: () => resolve('signed-in'),
        onFailure: (error) => reject(error),
        newPasswordRequired: (attributes) => {
          // Cognito rejects these read-only attributes when they are sent back.
          delete attributes.email_verified;
          delete attributes.phone_number_verified;
          this.pendingUser = user;
          this.pendingAttributes = attributes;
          resolve('new-password-required');
        },
        mfaRequired: () => reject(new Error(MFA_MESSAGE)),
        totpRequired: () => reject(new Error(MFA_MESSAGE)),
      });
    });
  }

  /** Second step of a first login: sets the permanent password. */
  completeNewPassword(newPassword: string): Promise<void> {
    const user = this.pendingUser;
    if (!user) {
      return Promise.reject(new Error('Your sign-in has expired. Please sign in again.'));
    }
    return new Promise<void>((resolve, reject) => {
      user.completeNewPasswordChallenge(newPassword, this.pendingAttributes, {
        onSuccess: () => {
          this.pendingUser = null;
          this.pendingAttributes = {};
          resolve();
        },
        onFailure: (error) => reject(error),
      });
    });
  }

  /** The current session, refreshed if needed, or null when nobody is signed in. */
  private session(): Promise<CognitoUserSession | null> {
    const user = this.pool.getCurrentUser();
    if (!user) {
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      user.getSession((error: Error | null, session: CognitoUserSession | null) => {
        resolve(!error && session && session.isValid() ? session : null);
      });
    });
  }

  isAuthenticated(): Promise<boolean> {
    return this.session().then((session) => !!session);
  }

  /** The bearer token for API calls (the ACCESS token, not the ID token); undefined when not signed in. Never rejects. */
  async getAccessToken(): Promise<string | undefined> {
    try {
      return (await this.session())?.getAccessToken().getJwtToken();
    } catch {
      return undefined;
    }
  }

  /**
   * task-59: both tokens of the current session, for reading identity claims rather than for
   * calling the API. A pre-token-generation trigger can put a custom claim on the ID token,
   * on the access token, or on both, depending on its version and which generation block it
   * fills, so a claim has to be looked for in either. The API is still sent the access token.
   */
  async getSessionTokens(): Promise<{ accessToken?: string; idToken?: string }> {
    try {
      const session = await this.session();
      return {
        accessToken: session?.getAccessToken().getJwtToken(),
        idToken: session?.getIdToken().getJwtToken(),
      };
    } catch {
      return {};
    }
  }

  /** Signs out locally and forgets the app's cookies; returns to the login page unless told not to. */
  signOut(navigate = true): void {
    this.pool.getCurrentUser()?.signOut();
    this.pendingUser = null;
    cookies.deleteAll();
    if (navigate) {
      navigateTo('/login');
    }
  }
}

export const auth = new AuthService();
